import {
	OPERATION_STATUS,
	IFormOperationAnswerStep,
	IFormOperationAnswerField,
	IOperationDetailResponse,
	IOperationFormDetail,
	OPERATION_TYPE,
} from '../models/operations.models';
import { resolveFormFieldAuthMatchDataControl } from '../../processes/utils/form-auth-match-data.util';
import { hasFormFieldMatchOcrKo, isFormOperationMatchOcrField } from './form-operation-detail-display.util';

/** Error de cotejo match_data en snapshot OCR embebido (`checks.ocr.match_data_errors`). */
export interface IFormMatchDataError {
	key: string;
	required_value?: string;
	response_value?: string;
	similarity?: number;
	min_similarity?: number;
}

/** Extrae errores de cotejo desde `data.FORM.checks.ocr.match_data_errors` (fallback legacy). */
export function resolveFormMatchDataErrors(
	form: IOperationFormDetail | null | undefined,
): IFormMatchDataError[] {
	const ocr = form?.checks?.ocr as { match_data_errors?: IFormMatchDataError[] } | undefined;
	if (!Array.isArray(ocr?.match_data_errors)) {
		return [];
	}
	return ocr.match_data_errors.filter((entry) => Boolean(entry?.key?.trim()));
}

export function buildFormMatchDataErrorKeySet(errors: IFormMatchDataError[] | null | undefined): Set<string> {
	return new Set((errors ?? []).map((entry) => entry.key.trim()).filter(Boolean));
}

/** Campo FORM con cotejo cuya clave auth aparece en `match_data_errors`. */
export function isFormFieldMatchDataMismatch(
	field: Pick<IFormOperationAnswerField, 'id' | 'name' | 'validators'>,
	errorKeys: Set<string>,
): boolean {
	if (!errorKeys.size || !isFormOperationMatchOcrField(field)) {
		return false;
	}
	const authKey = resolveFormFieldAuthMatchDataControl(field);
	return authKey ? errorKeys.has(authKey) : false;
}

export function hasFormStepMatchDataKo(
	fields: Pick<IFormOperationAnswerField, 'id' | 'name' | 'validators'>[] | undefined,
	errorKeys: Set<string>,
): boolean {
	return (fields ?? []).some((field) => isFormFieldMatchDataMismatch(field, errorKeys));
}

function listFormMatchOcrFields(form: IOperationFormDetail): IFormOperationAnswerField[] {
	const fromAnswers = (form.answers ?? []).flatMap((step) => step.fields ?? []);
	const fromActivity = (form.step_activity ?? []).flatMap((step) => {
		const snapshot = step.response_snapshot as IFormOperationAnswerStep | undefined;
		return snapshot?.fields ?? [];
	});
	return [...fromAnswers, ...fromActivity];
}

/** Algún campo con `match_ocr_result.status === KO` en answers o step_activity. */
export function hasFormOperationMatchOcrKoInPayload(form: IOperationFormDetail | null | undefined): boolean {
	if (!form) {
		return false;
	}
	return listFormMatchOcrFields(form).some((field) => field.match_ocr_result?.status === 'KO');
}

/** Hay fallo de cotejo match_data / match_ocr en el payload FORM. */
export function hasFormOperationMatchDataKo(form: IOperationFormDetail | null | undefined): boolean {
	if (!form) {
		return false;
	}
	return resolveFormMatchDataErrors(form).length > 0 || hasFormOperationMatchOcrKoInPayload(form);
}

/**
 * Estado efectivo FORM: KO si el back ya lo marca, hay `match_data_errors`
 * o algún `match_ocr_result` en KO (contrato pwcs post-deploy).
 */
export function resolveFormOperationEffectiveState(
	form: IOperationFormDetail | null | undefined,
): OPERATION_STATUS | string | undefined {
	if (!form) {
		return undefined;
	}
	if (String(form.state ?? '').toUpperCase() === OPERATION_STATUS.KO) {
		return OPERATION_STATUS.KO;
	}
	if (hasFormOperationMatchDataKo(form)) {
		return OPERATION_STATUS.KO;
	}
	return form.state;
}

/** Estado efectivo en detalle de operación (header, tile Resultado). */
export function resolveOperationDetailEffectiveState(
	detail: IOperationDetailResponse | null | undefined,
): string | undefined {
	if (!detail) {
		return undefined;
	}
	if (detail.operation_type_name === OPERATION_TYPE.FORM) {
		const form = detail.data as unknown as IOperationFormDetail;
		const resolved = resolveFormOperationEffectiveState(form) ?? detail.state_name;
		return normalizeOperationStateName(resolved);
	}
	return normalizeOperationStateName(detail.state_name);
}

function normalizeOperationStateName(state: unknown): string | undefined {
	if (state == null) {
		return undefined;
	}
	const normalized = String(state).trim().toUpperCase();
	if (!normalized || normalized === 'NULL' || normalized === 'UNDEFINED') {
		return undefined;
	}
	return normalized;
}

export function hasFormStepActivityKo(
	step: Pick<IFormOperationAnswerStep, 'fields'> | undefined,
	errorKeys: Set<string>,
): boolean {
	return hasFormStepMatchDataKo(step?.fields, errorKeys) || hasFormFieldMatchOcrKo(step?.fields);
}
